import React, { createContext, useContext, useEffect, useState } from "react";
import { getFx, getAtlasMarkets } from "../lib/api";

// ISO-2 -> presentation currency. "EU" = All Europe (search across markets).
const META = {
  EU: { label: "All Europe", currency: "EUR" },
  NL: { label: "Netherlands", currency: "EUR" }, DE: { label: "Germany", currency: "EUR" },
  BE: { label: "Belgium", currency: "EUR" }, FR: { label: "France", currency: "EUR" },
  ES: { label: "Spain", currency: "EUR" }, IT: { label: "Italy", currency: "EUR" },
  AT: { label: "Austria", currency: "EUR" }, LU: { label: "Luxembourg", currency: "EUR" },
  PT: { label: "Portugal", currency: "EUR" }, PL: { label: "Poland", currency: "PLN" },
  SE: { label: "Sweden", currency: "SEK" }, DK: { label: "Denmark", currency: "DKK" },
  CZ: { label: "Czech Republic", currency: "CZK" }, RO: { label: "Romania", currency: "RON" },
  HU: { label: "Hungary", currency: "HUF" }, UK: { label: "United Kingdom", currency: "GBP" },
};

// Reasonable fallback list until the live available-markets call returns.
const FALLBACK = ["EU", "NL", "DE", "BE", "FR", "ES", "IT", "AT"].map((c) => ({ code: c, ...META[c] }));

const KEY = "atlas_market";
const MarketContext = createContext(null);

// Detect the user's country from the browser locale region subtag (e.g. "nl-NL"
// -> NL). Used ONLY to pick a sensible default market; never locks the user in.
const detectCountry = () => {
  try {
    const langs = navigator.languages && navigator.languages.length ? navigator.languages : [navigator.language];
    for (const l of langs) {
      const region = (l || "").split("-")[1];
      if (region && META[region.toUpperCase()]) return region.toUpperCase();
    }
  } catch { /* ignore */ }
  return null;
};

export const MarketProvider = ({ children }) => {
  const stored = (() => { try { return localStorage.getItem(KEY); } catch { return null; } })();
  const detected = detectCountry();
  // stored manual choice wins; else geolocation default; else All Europe.
  const [code, setCode] = useState(stored || detected || "EU");
  const [auto, setAuto] = useState(!stored); // default came from detection/fallback, not a manual choice
  const [markets, setMarkets] = useState(FALLBACK);
  const [fx, setFx] = useState({ rates: null, date: null, source: null, available: false });

  useEffect(() => { getFx().then(setFx).catch(() => setFx({ rates: null, available: false })); }, []);

  useEffect(() => {
    getAtlasMarkets()
      .then((d) => {
        const list = (d.markets || []).map((m) => ({ code: m.code, label: m.label, currency: m.currency, count: m.count }));
        if (list.length) {
          setMarkets(list);
          // If the auto/stored code isn't actually available, fall back to All Europe.
          setCode((c) => (list.some((m) => m.code === c) ? c : "EU"));
        }
      })
      .catch(() => { /* keep fallback */ });
  }, []);

  const setMarket = (c) => {
    setCode(c);
    setAuto(false);
    try { localStorage.setItem(KEY, c); } catch { /* ignore */ }
  };

  const market = markets.find((m) => m.code === code) || { code, ...(META[code] || { label: code, currency: "EUR" }) };

  const convert = (amount, fromCurrency, toCurrency) => {
    if (amount == null || !fromCurrency || !toCurrency) return null;
    const rates = fx.rates;
    if (!rates) return null;
    const rf = fromCurrency === "EUR" ? 1 : rates[fromCurrency];
    const rt = toCurrency === "EUR" ? 1 : rates[toCurrency];
    if (!rf || !rt) return null;
    return (amount / rf) * rt;
  };

  const toMarket = (amount, fromCurrency) => {
    if (fromCurrency && market.currency === fromCurrency) return amount;
    return convert(amount, fromCurrency, market.currency);
  };

  return (
    <MarketContext.Provider value={{ market, setMarket, markets, auto, fx, toMarket, convert }}>
      {children}
    </MarketContext.Provider>
  );
};

export const useMarket = () => useContext(MarketContext);
